import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './Health.css';

const REQUEST_TIMEOUT_MS = 30000;
const CONCURRENCY = 4;

const methodClass = (method) => `health-method health-method-${method.toLowerCase()}`;

const formatCheckedAt = (value) => {
  if (!value) return 'Not checked yet';
  return new Date(value).toLocaleString();
};

const Health = () => {
  const { fetchWithAuth } = useAuth();
  const [checks, setChecks] = useState([]);
  const [systemChecks, setSystemChecks] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [lastRunAt, setLastRunAt] = useState(null);
  const [e2eResult, setE2eResult] = useState(null);
  const [e2eRunning, setE2eRunning] = useState(false);
  const [e2eError, setE2eError] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [scheduleRunning, setScheduleRunning] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  const updateCheck = useCallback((key, patch) => {
    setChecks((current) => current.map((item) => (
      item.key === key ? { ...item, ...patch } : item
    )));
  }, []);

  const runChecks = useCallback(async (endpointList) => {
    const requestChecks = endpointList.filter((item) => item.checkMode === 'request');
    if (requestChecks.length === 0) return;

    setRunning(true);
    setError(null);
    setChecks((current) => current.map((item) => (
      item.checkMode === 'request'
        ? { ...item, status: 'queued', statusCode: null, responseTimeMs: null, error: null }
        : item
    )));

    let cursor = 0;
    const worker = async () => {
      while (cursor < requestChecks.length) {
        const check = requestChecks[cursor];
        cursor += 1;
        updateCheck(check.key, { status: 'checking' });

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        const startedAt = performance.now();

        try {
          const response = await fetchWithAuth(check.probePath, { signal: controller.signal });
          const responseTimeMs = Math.round(performance.now() - startedAt);
          const accepted = check.expectedStatuses.includes(response.status);
          let requestError = null;

          if (!accepted) {
            try {
              const body = await response.json();
              requestError = body.error || body.message || response.statusText;
            } catch (_error) {
              requestError = response.statusText || `HTTP ${response.status}`;
            }
          }

          updateCheck(check.key, {
            status: accepted ? 'healthy' : 'unhealthy',
            statusCode: response.status,
            responseTimeMs,
            error: requestError,
            checkedAt: new Date().toISOString(),
          });
        } catch (requestError) {
          updateCheck(check.key, {
            status: 'unhealthy',
            statusCode: null,
            responseTimeMs: Math.round(performance.now() - startedAt),
            error: requestError.name === 'AbortError'
              ? `Timed out after ${REQUEST_TIMEOUT_MS / 1000}s`
              : requestError.message,
            checkedAt: new Date().toISOString(),
          });
        } finally {
          clearTimeout(timeout);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, requestChecks.length) }, () => worker())
    );
    setLastRunAt(new Date().toISOString());
    setRunning(false);
  }, [fetchWithAuth, updateCheck]);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth('/api/health/endpoints');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load endpoint catalog');

      const endpointList = data.endpoints.map((endpoint) => ({
        ...endpoint,
        key: `${endpoint.method} ${endpoint.path}`,
        status: endpoint.checkMode === 'registered' ? 'registered' : 'queued',
        statusCode: null,
        responseTimeMs: null,
        checkedAt: null,
        error: null,
      }));

      setSystemChecks(data.checks);
      setChecks(endpointList);
      setCatalogLoading(false);
      await runChecks(endpointList);
    } catch (loadError) {
      setError(loadError.message);
      setCatalogLoading(false);
    }
  }, [fetchWithAuth, runChecks]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const loadSchedule = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/health/schedule');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load daily schedule');
      setSchedule(data);
      setScheduleError(null);
    } catch (loadError) {
      setScheduleError(loadError.message);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadSchedule();
    const timer = setInterval(loadSchedule, 60000);
    return () => clearInterval(timer);
  }, [loadSchedule]);

  const summary = useMemo(() => checks.reduce((result, item) => {
    result.total += 1;
    if (item.status === 'healthy') result.healthy += 1;
    if (item.status === 'unhealthy') result.unhealthy += 1;
    if (item.status === 'registered') result.registered += 1;
    if (item.status === 'checking' || item.status === 'queued') result.checking += 1;
    return result;
  }, { total: 0, healthy: 0, unhealthy: 0, registered: 0, checking: 0 }), [checks]);

  const visibleChecks = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return checks.filter((item) => {
      const matchesQuery = !normalizedQuery
        || item.path.toLowerCase().includes(normalizedQuery)
        || item.method.toLowerCase().includes(normalizedQuery);
      const matchesFilter = filter === 'all' || item.status === filter;
      return matchesQuery && matchesFilter;
    });
  }, [checks, filter, query]);

  const runE2E = async () => {
    setE2eRunning(true);
    setE2eError(null);
    setE2eResult(null);
    try {
      const response = await fetchWithAuth('/api/health/e2e', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to run E2E health check');
      setE2eResult(data);
    } catch (runError) {
      setE2eError(runError.message);
    } finally {
      setE2eRunning(false);
    }
  };
  const runScheduledReport = async () => {
    setScheduleRunning(true);
    setScheduleError(null);
    try {
      const response = await fetchWithAuth('/api/health/schedule/run', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to run the daily report');
      setE2eResult(data);
      await loadSchedule();
    } catch (runError) {
      setScheduleError(runError.message);
    } finally {
      setScheduleRunning(false);
    }
  };
  const rerun = async () => {
    await runChecks(checks);
    await runE2E();
  };
  const quickChecksPassed = summary.unhealthy === 0 && summary.checking === 0 && summary.total > 0;
  const e2ePassed = e2eResult?.status === 'healthy';
  const allOperational = quickChecksPassed && e2ePassed;
  const checksInProgress = running || e2eRunning || scheduleRunning;
  const overallText = checksInProgress
    ? 'Checks in progress'
    : !quickChecksPassed
      ? 'Some checks need attention'
      : !e2eResult
        ? 'Quick checks passed · E2E journey not run'
        : e2ePassed
          ? 'All systems operational'
          : 'E2E journey needs attention';
  const databaseHealthy = systemChecks?.database?.status === 'healthy';

  return (
    <div className="health-page">
      <div className="health-heading">
        <div>
          <h1>Endpoint Health</h1>
          <p>Live smoke checks for safe requests and route registration for mutating endpoints.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary health-run-button"
          onClick={rerun}
          disabled={checksInProgress || catalogLoading || checks.length === 0}
        >
          {checksInProgress ? 'Checking…' : 'Run all checks'}
        </button>
      </div>

      {error && <div className="error">Error: {error}</div>}

      <div className={`health-overall ${allOperational ? 'is-healthy' : 'is-unhealthy'}`}>
        <span className="health-overall-dot" />
        <div>
          <strong>{overallText}</strong>
          <span>Last endpoint check: {formatCheckedAt(lastRunAt)}</span>
        </div>
      </div>

      <div className="health-summary-grid">
        <div className="health-summary-card">
          <span>Total routes</span>
          <strong>{summary.total}</strong>
        </div>
        <div className="health-summary-card health-summary-success">
          <span>Healthy</span>
          <strong>{summary.healthy}</strong>
        </div>
        <div className="health-summary-card health-summary-danger">
          <span>Failed</span>
          <strong>{summary.unhealthy}</strong>
        </div>
        <div className="health-summary-card health-summary-info">
          <span>Route only</span>
          <strong>{summary.registered}</strong>
        </div>
      </div>

      <div className="health-services">
        <div className="health-service-card">
          <span className={`health-service-dot ${systemChecks ? 'is-up' : 'is-pending'}`} />
          <div>
            <strong>API server</strong>
            <span>{systemChecks ? 'Available' : 'Checking…'}</span>
          </div>
        </div>
        <div className="health-service-card">
          <span className={`health-service-dot ${databaseHealthy ? 'is-up' : systemChecks ? 'is-down' : 'is-pending'}`} />
          <div>
            <strong>Database</strong>
            <span>
              {systemChecks
                ? `${databaseHealthy ? 'Connected' : 'Unavailable'} · ${systemChecks.database.responseTimeMs} ms`
                : 'Checking…'}
            </span>
          </div>
        </div>
      </div>

      <div className="card health-schedule-card">
        <div className="health-schedule-heading">
          <div>
            <div className="health-e2e-title-row">
              <h2 className="card-title">Daily Telegram report</h2>
              <span className={`health-schedule-badge ${schedule?.configured && schedule?.enabled ? 'is-enabled' : ''}`}>
                {schedule?.enabled === false
                  ? 'DISABLED'
                  : schedule?.configured
                    ? 'ENABLED'
                    : 'SETUP REQUIRED'}
              </span>
            </div>
            <p>
              Runs the full registration, login and CRUD journey once per day and sends the result to Telegram.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary health-e2e-button"
            onClick={runScheduledReport}
            disabled={scheduleRunning || running || e2eRunning || !schedule?.configured || !schedule?.enabled}
          >
            {scheduleRunning ? 'Running and sending…' : 'Run today now'}
          </button>
        </div>

        <div className="health-schedule-grid">
          <div>
            <span>Schedule</span>
            <strong>{schedule ? `${schedule.time} · ${schedule.timezone}` : 'Loading…'}</strong>
          </div>
          <div>
            <span>Next run</span>
            <strong>{schedule?.nextRunLocal || 'Waiting for Telegram settings'}</strong>
          </div>
          <div>
            <span>Channel</span>
            <strong>{schedule?.chatId || 'Not configured'}</strong>
          </div>
          <div>
            <span>Last report</span>
            <strong>
              {schedule?.lastResult
                ? `${schedule.lastResult.status} · Telegram ${schedule.lastResult.notificationStatus}`
                : 'Not run yet'}
            </strong>
          </div>
        </div>

        {!schedule?.configured && (
          <div className="health-e2e-warning">
            Set TELEGRAM_BOT_TOKEN and TELEGRAM_CHAT_ID in the server environment, then restart the admin server.
          </div>
        )}
        {schedule?.configured && schedule?.enabled && (
          <div className="health-schedule-note">
            “Run today now” performs the daily run immediately and counts as today’s single scheduled check.
          </div>
        )}
        {scheduleError && <div className="error health-e2e-error">Error: {scheduleError}</div>}
      </div>

      <div className="card health-e2e-card">
        <div className="health-e2e-heading">
          <div>
            <div className="health-e2e-title-row">
              <h2 className="card-title">Full user journey</h2>
              <span className="health-environment-badge">TEST API</span>
            </div>
            <p>
              Registration, login and complete CRUD flows for foods, weights, measurements,
              fitness activities and fitness settings.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary health-e2e-button"
            onClick={runE2E}
            disabled={e2eRunning || running || scheduleRunning}
          >
            {e2eRunning ? 'Running journey…' : 'Run E2E journey'}
          </button>
        </div>

        <div className="health-e2e-warning">
          A unique temporary account is created. Test records are cleaned up and the account is deactivated even if a step fails.
        </div>

        {e2eError && <div className="error health-e2e-error">Error: {e2eError}</div>}

        {e2eRunning && (
          <div className="health-e2e-progress">
            <span />
            Running the steps sequentially. This can take up to a minute…
          </div>
        )}

        {e2eResult && (
          <>
            <div className={`health-e2e-result ${e2eResult.status === 'healthy' ? 'is-healthy' : 'is-unhealthy'}`}>
              <div>
                <strong>
                  {e2eResult.status === 'healthy' ? 'Full journey passed' : 'Journey has failed steps'}
                </strong>
                <span>{e2eResult.target} · {e2eResult.durationMs} ms</span>
              </div>
              <div className="health-e2e-counts">
                <span>{e2eResult.summary.healthy} passed</span>
                <span>{e2eResult.summary.unhealthy} failed</span>
              </div>
            </div>

            <div className="table-container health-e2e-table-container">
              <table className="health-table health-e2e-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Step</th>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th>HTTP</th>
                    <th>Response</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {e2eResult.steps.map((step, index) => (
                    <tr key={`${step.name}-${index}`}>
                      <td>
                        <span className={`health-status health-status-${step.status}`}>
                          <span />
                          {step.status}
                        </span>
                      </td>
                      <td>{step.name}</td>
                      <td><span className={methodClass(step.method)}>{step.method}</span></td>
                      <td><code>{step.path}</code></td>
                      <td>{step.statusCode ?? '—'}</td>
                      <td>{step.responseTimeMs} ms</td>
                      <td className="health-details">{step.error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card health-table-card">
        <div className="health-toolbar">
          <input
            className="input health-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search endpoints…"
            aria-label="Search endpoints"
          />
          <div className="health-filters" aria-label="Filter endpoint checks">
            {[
              ['all', 'All'],
              ['healthy', 'Healthy'],
              ['unhealthy', 'Failed'],
              ['registered', 'Route only'],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={filter === value ? 'active' : ''}
                onClick={() => setFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {catalogLoading ? (
          <div className="loading">Loading endpoints…</div>
        ) : (
          <div className="table-container">
            <table className="health-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Endpoint</th>
                  <th>HTTP</th>
                  <th>Response</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {visibleChecks.map((check) => (
                  <tr key={check.key}>
                    <td>
                      <span className={`health-status health-status-${check.status}`}>
                        <span />
                        {check.status === 'queued'
                          ? 'Queued'
                          : check.status === 'registered'
                            ? 'Route only'
                            : check.status}
                      </span>
                    </td>
                    <td><span className={methodClass(check.method)}>{check.method}</span></td>
                    <td><code>{check.path}</code></td>
                    <td>{check.statusCode ?? '—'}</td>
                    <td>{check.responseTimeMs == null ? '—' : `${check.responseTimeMs} ms`}</td>
                    <td className="health-details">
                      {check.error
                        || (check.checkMode === 'registered'
                          ? 'Route exists · request not sent'
                          : check.statusCode === 404
                            ? 'Expected not found response'
                            : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visibleChecks.length === 0 && (
              <div className="health-empty">No endpoints match this filter.</div>
            )}
          </div>
        )}
      </div>

      <p className="health-note">
        The quick check calls GET endpoints with small, read-only payloads. Mutating routes are exercised only by the isolated E2E journey against the configured test API.
      </p>
    </div>
  );
};

export default Health;
